import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import CopyButton from "@galaxy-io/dls/buttons/CopyButton";
import CopyInput from "@galaxy-io/dls/inputs/CopyInput";
import { InputVariant } from "@galaxy-io/dls/inputs/Input";
import Box from "@galaxy-io/dls/layout/Box";
import Text, { TextSize, TextVariant } from "@galaxy-io/dls/text/Text";
import { t } from "@galaxy-io/dls/theme/tokens/t";
import Widget, { WidgetVariant } from "@galaxy-io/dls/widget/Widget";
import {
  anthropicModels,
  type OutboundChannel,
  type OutboundProposal,
  outboundChannels,
} from "@gtm/contracts/integrations/anthropic";
import { styled } from "@linaria/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useRouter } from "@/app/navigation";
import { QueryState } from "@/components/query-state";
import { Button } from "@/components/ui/button";
import { SelectField } from "@/components/workspace/select-field";
import { SourceMarkdown } from "@/components/workspace/source-markdown";
import { SurfaceDialog } from "@/components/workspace/surface-dialog";
import { apiClient, unwrap } from "@/lib/api-client";

type Model = (typeof anthropicModels)[number];
const modelLabels: Record<Model, string> = {
  "claude-opus-5": "Claude Opus 5",
  "claude-sonnet-5": "Claude Sonnet 5",
  "claude-haiku-4-5": "Claude Haiku 4.5",
};

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${t.space[12]};
  padding: ${t.space[12]} ${t.space[16]} ${t.space[16]};
  min-width: 0;
`;
// A multi-line sibling of CopyInput: the same hairline surface, the copy
// button beside the label because the text wraps. The draft renders as
// Markdown; copying still hands over the Markdown source.
const channelLabels: Record<OutboundChannel, string> = {
  email: "Email",
  linkedin: "LinkedIn message",
  twitter: "X direct message",
};

/** True once the workspace has an enabled Anthropic connection. */
export function useAnthropicConfigured() {
  const query = useQuery({
    queryKey: ["integrations"],
    staleTime: 30_000,
    queryFn: ({ signal }) => unwrap(apiClient().GET("/integrations", { signal })),
  });
  return {
    loaded: !!query.data,
    configured: !!query.data?.some((c) => c.provider === "anthropic" && c.enabled),
  };
}
function CopyBlock({ label, value }: { label: string; value: string }) {
  return (
    <Widget
      header={label}
      variant={WidgetVariant.SECONDARY}
      actions={
        <CopyButton
          ariaLabel={`Copy ${label.toLowerCase()}`}
          value={value}
          size={ButtonSize.SMALL}
        />
      }
    >
      <Box aria-label={label} maxHeight="40dvh" minHeight={96} overflow="auto">
        <SourceMarkdown content={value} breaks />
      </Box>
    </Widget>
  );
}

/**
 * Explicit channel and shared prompt selection before generating a draft.
 * Every output field is copyable; nothing is sent.
 */
export function Outbound({ personId }: { personId: string }) {
  const anthropic = useAnthropicConfigured();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [channel, setChannel] = useState<OutboundChannel | null>(null);
  const [model, setModel] = useState<Model>("claude-haiku-4-5");
  const [promptKey, setPromptKey] = useState("");
  const prompts = useQuery({
    queryKey: ["knowledge-prompts"],
    queryFn: () => unwrap(apiClient().GET("/knowledge/prompts")),
    enabled: open && anthropic.configured,
  });
  const [draft, setDraft] = useState<OutboundProposal | null>(null);
  const proposal = useMutation({
    mutationFn: (body: { channel: OutboundChannel; model: Model; promptKey: string | null }) =>
      unwrap(
        apiClient().POST("/people/{id}/outbound-proposal", {
          params: { path: { id: personId } },
          body,
        }),
      ),
    onSuccess: setDraft,
  });
  function generate(next: OutboundChannel, using: Model = model) {
    setChannel(next);
    setDraft(null);
    proposal.mutate({ channel: next, model: using, promptKey: promptKey || null });
  }
  function start() {
    setModel("claude-haiku-4-5");
    setChannel(null);
    setDraft(null);
    proposal.reset();
    setOpen(true);
  }
  const compose = draft &&
    draft.channel === "email" && {
      to: draft.to || "",
      subject: draft.subject || "",
      body: draft.body,
    };
  const mailto =
    compose &&
    `mailto:${encodeURIComponent(compose.to)}?subject=${encodeURIComponent(compose.subject)}&body=${encodeURIComponent(compose.body)}`;
  const gmail =
    compose &&
    `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(compose.to)}&su=${encodeURIComponent(compose.subject)}&body=${encodeURIComponent(compose.body)}`;
  return (
    <>
      <Button
        label="Outbound"
        variant={ButtonVariant.SECONDARY}
        isDisabled={!anthropic.loaded}
        onClick={start}
      />
      {
        <SurfaceDialog
          open={open}
          flush
          title="Outbound"
          headerDescription={
            anthropic.configured
              ? draft
                ? `Drafted by ${draft.model} from the profile and recent signals.`
                : proposal.isPending
                  ? "Drafting from the profile and recent signals…"
                  : "Choose a channel and prompt, then generate a draft."
              : "Outbound drafts need an Anthropic connection."
          }
          onClose={() => setOpen(false)}
          busy={proposal.isPending}
          footer={
            anthropic.configured ? (
              <>
                <Button
                  label={draft ? "Regenerate" : "Generate draft"}
                  variant={ButtonVariant.SECONDARY}
                  isDisabled={
                    proposal.isPending || !channel || prompts.isPending || !!prompts.error
                  }
                  onClick={() => channel && generate(channel)}
                />
                {mailto && gmail && (
                  <>
                    <Button
                      href={gmail}
                      isExternal
                      label="Open in Gmail"
                      variant={ButtonVariant.SECONDARY}
                    />
                    <Button href={mailto} label="Open in mail" />
                  </>
                )}
              </>
            ) : (
              <Button
                label="Connect Anthropic"
                onClick={() => {
                  setOpen(false);
                  router.push("/integrations?integration=new-anthropic");
                }}
              />
            )
          }
        >
          <Body>
            {!anthropic.configured ? (
              <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
                Connect Anthropic under Integrations, then create shared prompts for your senders
                and variations in Configuration → Knowledge Center.
              </Text>
            ) : (
              <>
                <SelectField
                  label="Model"
                  value={model}
                  disabled={proposal.isPending}
                  options={anthropicModels.map((value) => ({
                    value,
                    label: modelLabels[value],
                  }))}
                  onChange={(value) => {
                    setModel(value as Model);
                    setDraft(null);
                    proposal.reset();
                  }}
                />
                <SelectField
                  label="Channel"
                  value={channel || ""}
                  placeholder="Choose a channel…"
                  disabled={proposal.isPending}
                  options={outboundChannels.map((value) => ({
                    value,
                    label: channelLabels[value],
                  }))}
                  onChange={(value) => {
                    setChannel(value as OutboundChannel);
                    setDraft(null);
                    proposal.reset();
                  }}
                />
                <SelectField
                  label="Prompt"
                  search
                  value={promptKey}
                  disabled={proposal.isPending || prompts.isPending}
                  options={[
                    { value: "", label: "Built-in instructions only" },
                    ...(prompts.data?.items ?? []).map((prompt) => ({
                      value: prompt.key,
                      label: prompt.title,
                    })),
                  ]}
                  onChange={(value) => {
                    setPromptKey(value);
                    setDraft(null);
                    proposal.reset();
                  }}
                />
                {prompts.error && (
                  <QueryState error={prompts.error} onRetry={() => void prompts.refetch()} />
                )}
                {proposal.error ? (
                  <QueryState
                    error={proposal.error}
                    onRetry={channel ? () => generate(channel) : undefined}
                  />
                ) : proposal.isPending ? (
                  <QueryState loadingMessage="Drafting outreach…" />
                ) : !draft ? null : (
                  <>
                    {draft.channel === "email" && (
                      <>
                        {(draft.emails.length ? draft.emails : [""]).map((email, index) => (
                          <CopyInput
                            key={email || index}
                            label={index === 0 ? "To" : "Also known email"}
                            value={email}
                            fillWidth
                            variant={InputVariant.BASE}
                          />
                        ))}
                        <CopyInput
                          label="Subject"
                          value={draft.subject || ""}
                          fillWidth
                          variant={InputVariant.BASE}
                        />
                      </>
                    )}
                    <CopyBlock
                      label={draft.channel === "email" ? "Body" : "Message"}
                      value={draft.body}
                    />
                  </>
                )}
              </>
            )}
          </Body>
        </SurfaceDialog>
      }
    </>
  );
}
